import { z } from "zod";
import { orderSchema, buyRequestSchema, buyResponseSchema, restingOrdersSchema, cancelResponseSchema, orderResolutionSchema, } from "../schemas/index.js";
/**
 * Generate a fresh UUID for an `Idempotency-Key`. Uses the platform Web Crypto
 * (Node ≥ 22 and browsers both expose `crypto.randomUUID`).
 */
export function generateIdempotencyKey() {
    const c = globalThis.crypto;
    if (c?.randomUUID)
        return c.randomUUID();
    // Extremely defensive fallback (should never hit on Node 22 / modern browsers).
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
/**
 * The execution surface: `GET /auth/orders` (blotter), `POST /auth/orders/buy`,
 * `GET /auth/orders/resting/me`, `POST /auth/orders/cancel`, and
 * `GET /auth/orders/:id/resolution`. For an `owner_self` org key the account is
 * inferred from the key (no `X-OpenMarkets-Account` header).
 */
export class OrdersResource {
    http;
    constructor(http) {
        this.http = http;
    }
    list(params = {}) {
        return this.http.request({
            method: "GET",
            path: "/auth/orders",
            endpoint: "orders.list",
            query: {
                status: params.status,
                contest_id: params.contest_id,
                settled: params.settled,
                result: params.result,
                currency: params.currency,
                mm: params.mm,
                model_id: params.model_id,
                opportunity_type: params.opportunity_type,
                sort: params.sort,
                from: params.from,
                to: params.to,
                cursor: params.cursor,
                limit: params.limit,
            },
            dataSchema: z.array(orderSchema),
        });
    }
    /**
     * Place one or more venue-directed legs. The body is validated client-side
     * against {@link buyRequestSchema} (strict + XOR rules) before sending so an
     * invalid leg fails fast with a clear message rather than a generic 400.
     * An `Idempotency-Key` header is always sent (caller-supplied or generated).
     */
    buy(request, opts = {}) {
        // Throws ZodError on an invalid leg (missing/both hashes, both amount+shares,
        // out-of-range max_price, unknown field). Callers can catch and surface — or
        // pass `skipValidation` to defer to the live API's own strict-body contract.
        const body = opts.skipValidation ? request : buyRequestSchema.parse(request);
        const key = opts.idempotencyKey ?? generateIdempotencyKey();
        return this.http.request({
            method: "POST",
            path: "/auth/orders/buy",
            endpoint: "orders.buy",
            headers: { "Idempotency-Key": key },
            body,
            dataSchema: buyResponseSchema,
        });
    }
    /** Working (unfilled / partially-filled) orders. Filled orders drop off. */
    resting() {
        return this.http.request({
            method: "GET",
            path: "/auth/orders/resting/me",
            endpoint: "orders.resting",
            dataSchema: restingOrdersSchema,
        });
    }
    /**
     * Cancel one or more orders by router_order_id. Ownership-checked per id — one
     * bad id fails the WHOLE batch with 403 `forbidden`.
     */
    cancel(routerOrderIds) {
        return this.http.request({
            method: "POST",
            path: "/auth/orders/cancel",
            endpoint: "orders.cancel",
            body: { router_order_ids: routerOrderIds },
            dataSchema: cancelResponseSchema,
        });
    }
    /** Settlement detail for a single order (OM verdict vs partner verdict). */
    resolution(routerOrderId) {
        return this.http.request({
            method: "GET",
            path: `/auth/orders/${encodeURIComponent(routerOrderId)}/resolution`,
            endpoint: "orders.resolution",
            dataSchema: orderResolutionSchema,
        });
    }
}
//# sourceMappingURL=orders.js.map